/**
 * Clone stamp (beyond SuperSplat): paint copies of the splats around a source
 * point onto another place, like an image editor's clone stamp but in 3D.
 *
 * A stroke has a fixed world offset (destination − source). Each destination
 * dab copies the live splats within its radius of `dab − offset` that the
 * stroke has not copied yet and, in replace mode, collects the live splats
 * within its radius of the dab itself, which the stroke paints over. The
 * caller previews `sources` moved by the offset and commits the stroke as one
 * CloneStampOp.
 */
import type { SplatEditModel } from './SplatEditModel';
import { SplatState } from './SplatState';
import { buildGrid, forEachNeighbour, type SpatialGrid } from './selection/spatial';

export interface Vec3Like { x: number; y: number; z: number }

/** Grid over the live (not hidden / deleted) world centers, for clone strokes. */
export function buildCloneGrid(model: SplatEditModel, cell: number): { grid: SpatialGrid; centers: Float32Array } {
    const centers = model.getWorldCenters();
    const include = new Uint8Array(model.numSplats);
    const state = model.state;
    for (let i = 0; i < model.numSplats; i++) {
        if (!(state[i] & (SplatState.hidden | SplatState.deleted))) include[i] = 1;
    }
    return { grid: buildGrid(centers, include, cell), centers };
}

export class CloneStroke {
    /** Rows to copy, in the order the stroke reached them. */
    readonly sources: number[] = [];
    /** Rows painted over (replace mode only). */
    readonly covered: number[] = [];
    readonly offset: Vec3Like;
    readonly replace: boolean;
    private readonly grid: SpatialGrid;
    private readonly centers: Float32Array;
    private readonly taken: Uint8Array;
    private readonly hit: Uint8Array;

    constructor(grid: SpatialGrid, centers: Float32Array, offset: Vec3Like, replace = false) {
        this.grid = grid;
        this.centers = centers;
        this.offset = { x: offset.x, y: offset.y, z: offset.z };
        this.replace = replace;
        const n = centers.length / 3;
        this.taken = new Uint8Array(n);
        this.hit = new Uint8Array(replace ? n : 0);
    }

    /** Add one destination dab (world point + radius); returns how many splats it newly copies. */
    addDab(x: number, y: number, z: number, radius: number): number {
        const before = this.sources.length;
        const { taken, sources } = this;
        forEachNeighbour(this.grid, this.centers, x - this.offset.x, y - this.offset.y, z - this.offset.z, radius, (j) => {
            if (!taken[j]) { taken[j] = 1; sources.push(j); }
        });
        if (this.replace) {
            const { hit, covered } = this;
            forEachNeighbour(this.grid, this.centers, x, y, z, radius, (j) => {
                if (!hit[j]) { hit[j] = 1; covered.push(j); }
            });
        }
        return this.sources.length - before;
    }
}
